import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BackBtn from "../widgets/BackBtn";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

// --- UTILITY FUNCTIONS FOR EMAIL GENERATION ---

const DEFAULT_DP = "https://via.placeholder.com/50";
const TimeZoneIndia = { timeZone: "Asia/Kolkata" };

export function getCatalogueTemplate(user, supplier, headerUrl, footerUrl) {
  const groupedCategories = (user.productCategories || []).reduce(
    (acc, { mainCat, subCat, subSubCat }) => {
      if (!acc[mainCat]) acc[mainCat] = {};
      if (!acc[mainCat][subCat]) acc[mainCat][subCat] = [];
      acc[mainCat][subCat].push(subSubCat);
      return acc;
    },
    {}
  );

  const formattedCategories = Object.entries(groupedCategories)
    .map(([mainCat, subCats]) => {
      const subCatHTML = Object.entries(subCats)
        .map(
          ([subCat, subSubCats]) =>
            `<em>${subCat}:</em> ${subSubCats.join(", ")}`
        )
        .join("; ");
      return `<strong>${mainCat}:</strong> ${subCatHTML}`;
    })
    .join("<br>");

  const sanitize = (input) => (input ? String(input).replace(/[<>]/g, "") : "");

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Catalogue</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f5f5f5; margin: 0; padding: 0; line-height: 1.6; color: #333; }
          .container { max-width: 800px; margin: 20px auto; padding: 24px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
          .section { border: 1px solid #e0e0e0; padding: 16px; margin: 16px 0; border-radius: 6px; }
          .profile-image { width: 100px; max-width: 100%; display: block; margin: 16px auto; border-radius: 50%; }
          table { width: 100%; border-collapse: collapse; margin: 24px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e0e0e0; }
          th { background-color: #f8f8f8; font-weight: 600; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          img.header-footer { width: 100%; max-width: 800px; height: auto; display: block; margin: 0 auto; border-radius: 6px; }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${sanitize(
            headerUrl
          )}" alt="Header Image" class="header-footer" />
          <div class="section">
            <p><strong>From:</strong> Company: ${sanitize(
              user.companyName
            )}, Name: ${sanitize(user.contactSalutation)} ${sanitize(
    user.contactFirstName
  )} ${sanitize(user.contactLastName)}, Phone: ${sanitize(
    user.mobile
  )}, Email: ${sanitize(user.contactEmail)}</p>
          </div>
          <div class="section">
            <p><strong>To:</strong> Company: ${sanitize(
              supplier.supplierName
            )},<br />Address: ${sanitize(supplier.address)}</p>
            <p>Phone: ${sanitize(supplier.phoneNumber)}</p>
            <p>Email: ${sanitize((supplier.emails || []).join(", "))}</p>
          </div>
          ${
            user.profile_image
              ? `<img src="${sanitize(
                  user.profile_image
                )}" alt="Profile Image" class="profile-image" />`
              : ""
          }
          <table>
            <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td><strong>Company Name:</strong></td><td>${sanitize(
                user.companyName
              )}</td></tr>
              <tr><td><strong>Product Categories:</strong></td><td>${formattedCategories}</td></tr>
            </tbody>
          </table>
          <img src="${sanitize(
            footerUrl
          )}" alt="Footer Image" class="header-footer" />
        </div>
      </body>
    </html>
  `.trim();
}

export function getExhibitorInfoOes(user, titleString) {
  function containsSetupAndDismantling(name) {
    const regex = /setup/i;
    const regexDismantling = /dismantling/i;
    return regex.test(name) && regexDismantling.test(name);
  }

  const shouldShowDates = containsSetupAndDismantling(titleString);

  return `<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
    <tr><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Attribute</th><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Value</th></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Company Name:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.companyName?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Raw Space:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.spaceType?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Size:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standSize?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Hall Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.hallNumber?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standNumber?.value || "N/A"
    }</td></tr>
    ${
      shouldShowDates
        ? `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Exhibits Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.exhibitArrival?.value
              ? new Date(user.exhibitArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Material Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.materialArrival?.value
              ? new Date(user.materialArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Dismantling Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.dismantling?.value
              ? new Date(user.dismantling.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
    `
        : ""
    }
  </table>`;
}

export function oesTemplate(
  user,
  templateBody = "",
  supplier,
  formTitle = "",
  headerUrl,
  footerUrl
) {
  return `<!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Email Template</title>
      <style>
          body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 0; width: 100%; -webkit-text-size-adjust: none; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; background-color: #ffffff; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; }
      </style>
  </head>
  <body>
      <div class="container">
          ${
            headerUrl
              ? `<img src="${headerUrl}" alt="Header Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>From:</strong> Company: ${
                user.companyName?.value || "N/A"
              }, Name: ${user.contactSalutation?.value || ""} ${
    user.contactFirstName?.value || ""
  } ${user.contactLastName?.value || ""}, Phone: ${
    user.mobile?.value || "N/A"
  }, Email: ${user.contactEmail?.value || "N/A"} </p>
          </div>
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>To:</strong> Company: ${
                supplier.supplierName || "N/A"
              },<br /> Address: ${supplier.address || "N/A"} </p>
              <p>Phone number: ${supplier.phoneNumber || "N/A"}</p>
              <p>Email: ${supplier.emails?.join(",") || "N/A"}</p>
          </div> 
          ${getExhibitorInfoOes(user, formTitle)}<br />
          ${templateBody}<br />
          ${
            footerUrl
              ? `<img src="${footerUrl}" alt="Footer Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
      </div>
  </body>
  </html>`;
}

function subTotal(rowIndex, headerIndex, myItems, headers) {
  const formulaString = headers[headerIndex]["formula"];
  const suffixes =
    formulaString
      .match(/key(\d+)/g)
      ?.map((match) => match.replace("key", "")) || [];

  const values = [];
  for (const suffix of suffixes) {
    const ci = parseInt(suffix) - 1;
    const type = headers[ci]?.type;
    let value = 0;
    if (type === "input number") {
      value = parseInt(myItems[rowIndex][`value${ci}`]) || 0;
    } else if (type === "label") {
      value = parseInt(myItems[rowIndex][`key${suffix}`]) || 0;
    } else if (type === "input checkbox") {
      value = myItems[rowIndex][`value${ci}`] ? 1 : 0;
    } else if (type === "label checkbox") {
      if (myItems[rowIndex][`value${ci}`]) {
        value =
          parseInt(myItems[rowIndex][`key${suffix}`]?.replace(/,/g, "")) || 0;
      } else {
        value = 0;
      }
    } else if (type === "input date range") {
      if (
        myItems[rowIndex][`dateTo${ci}`] &&
        myItems[rowIndex][`dateFrom${ci}`]
      ) {
        const dateFrom = new Date(myItems[rowIndex][`dateFrom${ci}`]);
        const dateTo = new Date(myItems[rowIndex][`dateTo${ci}`]);
        const diffInDays =
          Math.abs((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
        value = diffInDays;
      }
    }
    values.push(value);
  }

  let updatedFormula = formulaString;
  suffixes.forEach((suffix, index) => {
    const keyPattern = new RegExp(`key${suffix}`, "g");
    updatedFormula = updatedFormula.replace(keyPattern, values[index]);
  });

  let total = 0;
  try {
    // eslint-disable-next-line no-eval
    total = eval(updatedFormula);
  } catch (error) {
    console.error("Error evaluating formula:", error);
  }
  return total;
}

function renderCell(header, row, i) {
  let cellHtml = "";
  if (header.type === "label") {
    const value = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "label checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    const label = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled> ${label}</td>`;
  } else if (header.type === "input text" || header.type === "input number") {
    const value = row[`value${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "input date range") {
    const dateFrom = row[`dateFrom${i}`]
      ? new Date(row[`dateFrom${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    const dateTo = row[`dateTo${i}`]
      ? new Date(row[`dateTo${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">From: ${dateTo}<br>To: ${dateFrom}</td>`;
  } else if (header.type === "input checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled></td>`;
  } else if (header.type === "calc") {
    const value = row[`calcVal${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else {
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"></td>`;
  }
  return cellHtml;
}

export function generateRatedItemsHTML(
  headers,
  itemsList,
  isColumnSelection = false,
  selectedColumn = null,
  totalRequired = false
) {
  const columnsWithCheckedItems = new Set();
  if (isColumnSelection && selectedColumn != null) {
    itemsList.forEach((item) => {
      headers.forEach((header, i) => {
        if (
          i !== selectedColumn &&
          (header.type === "input checkbox" ||
            header.type === "label checkbox") &&
          item[`value${i}`]
        ) {
          columnsWithCheckedItems.add(i);
        }
      });
    });
  }

  const itemsWithSubtotal = itemsList.map((item, rowIndex) => {
    const newItem = { ...item };
    if (isColumnSelection && selectedColumn != null) {
      const isChecked = item[`value${selectedColumn}`];
      const valueKey = `key${selectedColumn + 1}`;
      const rawValue = item[valueKey];
      const value = parseFloat(String(rawValue).replace(/,/g, "")) || 0;
      newItem.subtotal = isChecked ? value : 0;
    } else {
      const calcIndex = headers.findIndex((item) => item.type === "calc");
      if (calcIndex !== -1) {
        newItem[`calcVal${calcIndex}`] = subTotal(
          rowIndex,
          calcIndex,
          itemsList,
          headers
        );
        newItem.subtotal = newItem[`calcVal${calcIndex}`] || 0;
      } else {
        newItem.subtotal = 0;
      }
    }
    return newItem;
  });

  const filteredItems = itemsWithSubtotal.filter((item) => {
    if (isColumnSelection && selectedColumn != null) {
      return item[`value${selectedColumn}`];
    } else {
      return item.subtotal > 0;
    }
  });

  const totalValue = totalRequired
    ? filteredItems.reduce((acc, item) => acc + (item.subtotal || 0), 0)
    : 0;

  let html =
    '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin-top: 15px;">';
  html += "<thead><tr>";
  headers.forEach((header, i) => {
    if (
      !isColumnSelection ||
      i === selectedColumn ||
      columnsWithCheckedItems.has(i) ||
      (header.type !== "input checkbox" && header.type !== "label checkbox")
    ) {
      html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">${header.header}</th>`;
    }
  });
  if (isColumnSelection && selectedColumn != null) {
    html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">Subtotal</th>`;
  }
  html += "</tr></thead><tbody>";

  filteredItems.forEach((row, rowIndex) => {
    html += "<tr>";
    headers.forEach((header, i) => {
      if (
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
      ) {
        html += renderCell(header, row, i);
      }
    });
    if (isColumnSelection && selectedColumn != null) {
      html += `<td style="padding: 10px; border: 1px solid #ddd;">${row.subtotal?.toLocaleString()}</td>`;
    }
    html += "</tr>";
  });
  html += "</tbody>";

  if (totalRequired) {
    const colspan = headers.filter(
      (header, i) =>
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
    ).length;
    const adjustedColspan =
      isColumnSelection && selectedColumn != null ? colspan : colspan - 1;

    html += "<tfoot>";
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">Total</td><td style="padding: 10px; border: 1px solid #ddd;">${totalValue?.toLocaleString()}</td></tr>`;
    const gst = Math.round(totalValue * 0.18);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">18% GST</td><td style="padding: 10px; border: 1px solid #ddd;">${gst?.toLocaleString()}</td></tr>`;
    const grandTotal = Math.round(totalValue + gst);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;"><strong>Grand Total</strong></td><td style="padding: 10px; border: 1px solid #ddd;"><strong>${grandTotal?.toLocaleString()}</strong></td></tr>`;
    html += "</tfoot>";
  }

  html += "</table>";
  return html;
}

export function generateMembersTableHTML(members) {
  let html = `
    <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; margin-top: 15px;">
      <thead>
        <tr style="background-color: #f2f2f2; font-weight: bold;">
          <th style="padding: 10px; border: 1px solid #ddd;">Profile Image</th>
          <th style="padding: 10px; border: 1px solid #ddd;">First Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Last Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Phone</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Email</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Role</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Designation</th>
        </tr>
      </thead>
      <tbody>
  `;
  (members || []).forEach((member) => {
    const profileImage = member.profile_image || DEFAULT_DP;
    html += `
      <tr>
        <td style="text-align: center; padding: 10px; border: 1px solid #ddd;">
          <img src="${profileImage}" alt="Profile Image" style="width: 50px; height: 50px; border-radius: 50%; display: block; margin: 0 auto;" />
        </td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.firstName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.lastName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.phone || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.email || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.role || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.designation || ""
        }</td>
      </tr>
    `;
  });
  html += `</tbody></table>`;
  return html;
}

export function generateBadgeEntitlementHTML(properties, members, user) {
  const {
    basenum = 0,
    basesqm = 0,
    extranum = 0,
    extrasqm = 1,
  } = properties || {};
  const intBasenum = parseInt(basenum, 10) || 0;
  const intBasesqm = parseInt(basesqm, 10) || 0;
  const intExtranum = parseInt(extranum, 10) || 0;
  const intExtrasqm = parseInt(extrasqm, 10) || 1;

  const mySize = parseFloat(user?.standSize?.value || 0);
  const totalFreeBadges =
    intBasenum +
    Math.ceil(Math.max(0, mySize - intBasesqm) * (intExtranum / intExtrasqm));
  const totalMembers = (members || []).length;
  const paidBadges = Math.max(0, totalMembers - totalFreeBadges);

  return `
    <div style="max-width: 500px; margin: 15px auto; font-family: Arial, sans-serif;">
      <div style="border: 1px solid #ddd; padding: 16px; border-radius: 8px; background-color: #f5f5f5;">
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
          <tbody>
            <tr>
              <td style="padding: 8px; color: #555;">Total Free Badges:</td>
              <td style="padding: 8px; text-align: right; color: #007bff; font-weight: bold;">${totalFreeBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Paid Badges Required:</td>
              <td style="padding: 8px; text-align: right; color: #ff4c4c; font-weight: bold;">${paidBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Total Members:</td>
              <td style="padding: 8px; text-align: right; color: #333; font-weight: bold;">${totalMembers}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

export function generateCartTemplate(cartItems) {
  let subtotal = 0;
  let itemRows = "";

  (cartItems || []).forEach((item) => {
    const itemTotal = (item.price || 0) * (item.quantity || 0);
    subtotal += itemTotal;
    itemRows += `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${
          item.name || ""
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${
          item.quantity || 0
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${(
          item.price || 0
        ).toFixed(2)}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${itemTotal.toFixed(
          2
        )}</td>
      </tr>
    `;
  });

  const gst = subtotal * 0.18;
  const grandTotal = subtotal + gst;

  return `
      <h2 style="margin-top: 20px;">Order Summary</h2>
      <table style="border-collapse: collapse; width: 100%; max-width: 600px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Item Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Quantity</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Price (₹)</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
        </tbody>
      </table>
      <p style="margin-top: 20px; font-size: 16px;"><strong>Subtotal:</strong> ₹${subtotal.toFixed(
        2
      )}</p>
      <p style="font-size: 16px;"><strong>GST (18%):</strong> ₹${gst.toFixed(
        2
      )}</p>
      <p style="font-size: 18px;"><strong>Grand Total:</strong> ₹${grandTotal.toFixed(
        2
      )}</p>
  `;
}

// --- FRONTEND EMAIL PREVIEW GENERATOR ---
const generateEmailPreview = (submission, headerUrl, footerUrl) => {
  const supplier = submission.selectedSupplier || {};

  // Mock User Object mapped from DB Company Name
  const mockUser = {
    companyName: { value: submission.exhibitorName || "Exhibitor Company" },
    spaceType: { value: "Bare Space" },
    standSize: { value: "18" },
    hallNumber: { value: "1" },
    standNumber: { value: "A101" },
    exhibitArrival: { value: new Date() },
    materialArrival: { value: new Date() },
    dismantling: { value: new Date() },
    contactSalutation: { value: "Mr." },
    contactFirstName: { value: "John" },
    contactLastName: { value: "Doe" },
    mobile: { value: "9876543210" },
    contactEmail: { value: "exhibitor@example.com" },
  };

  const mockMembers = [];

  let myTemplate = `<table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; margin-top: 20px;">
    <thead><tr></tr></thead><tbody>`;

  if (submission.form && Array.isArray(submission.form)) {
    submission.form.forEach((form) => {
      if (form.type === "DynamicTable") {
        const { properties } = form;
        let tableHtml = `<tr><td colspan="2"><table style="width: 100%; border-collapse: collapse;"><thead><tr>`;
        properties.headers?.forEach((header) => {
          tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: left;">${header.header}</th>`;
        });
        tableHtml += `</tr></thead><tbody>`;
        properties.value?.forEach((row) => {
          tableHtml += "<tr>";
          properties.headers?.forEach((header) => {
            const cell = row[header.key] !== undefined ? row[header.key] : "";
            tableHtml += `<td style="border: 1px solid #ddd; padding: 8px;">${cell}</td>`;
          });
          tableHtml += "</tr>";
        });
        tableHtml += `</tbody></table></td></tr>`;
        myTemplate += tableHtml;
      } else if (
        [
          "LabelInput",
          "Select",
          "TextArea",
          "TextField",
          "RadioGroup",
        ].includes(form.type)
      ) {
        if (form.properties?.value) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${form.properties?.value}</td>
          </tr>`;
        }
      } else if (form.type === "Checkbox") {
        if (form.properties?.checked) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><input type="checkbox" checked disabled></td>
          </tr>`;
        }
      } else if (form.type === "DatePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleDateString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "TimePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleTimeString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "RatedItems") {
        const { properties } = form;
        myTemplate += `<tr><td colspan="2">${generateRatedItemsHTML(
          properties.headers || [],
          properties.items || [],
          properties.columnSelection,
          properties.selectedColumn,
          properties.showTotal
        )}</td></tr>`;
      } else if (form.type === "TeamMembers") {
        myTemplate += `
          <tr><td colspan="2">${generateBadgeEntitlementHTML(
            form.properties,
            mockMembers,
            mockUser
          )}</td></tr>
          <tr><td colspan="2">${generateMembersTableHTML(mockMembers)}</td></tr>
        `;
      } else if (form.type === "PurchaseItems") {
        myTemplate += `<tr><td colspan="2">${generateCartTemplate(
          form.properties?.value || []
        )}</td></tr>`;
      }
    });
  }

  myTemplate += `</tbody></table>`;

  const finalHeaderUrl =
    headerUrl || "https://via.placeholder.com/800x150?text=Event+Header";
  const finalFooterUrl =
    footerUrl || "https://via.placeholder.com/800x100?text=Event+Footer";

  return oesTemplate(
    mockUser,
    myTemplate,
    supplier,
    submission.name,
    finalHeaderUrl,
    finalFooterUrl
  );
};

const AnalyticsBreakdown = ({
  counts = {},
  color = "#2563eb",
  emptyText = "No analytics available",
  title = "Analytics details",
  previewLimit = 4,
}) => {
  const [fullListOpen, setFullListOpen] = useState(false);
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const maxCount = entries[0]?.[1] || 1;
  const previewEntries = entries.slice(0, previewLimit);
  const remainingCount = Math.max(0, entries.length - previewLimit);

  const renderEntry = ([name, count]) => {
    const percentage = Math.max(6, Math.round((count / maxCount) * 100));

    return (
      <Box
        key={name}
        sx={{
          p: 1.5,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          bgcolor: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#1e293b",
              fontWeight: 600,
              lineHeight: 1.35,
              wordBreak: "break-word",
            }}
          >
            {name}
          </Typography>

          <Chip
            label={`${count} ${count === 1 ? "submission" : "submissions"}`}
            size="small"
            sx={{
              flexShrink: 0,
              bgcolor: `${color}14`,
              color,
              fontWeight: 800,
              fontSize: "0.72rem",
              border: `1px solid ${color}2b`,
            }}
          />
        </Box>

        <Box
          sx={{
            height: 6,
            bgcolor: "#eef2f7",
            borderRadius: 99,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: "100%",
              bgcolor: color,
              borderRadius: 99,
            }}
          />
        </Box>
      </Box>
    );
  };

  if (entries.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #cbd5e1",
          borderRadius: 2,
          bgcolor: "#f8fafc",
        }}
      >
        <Typography variant="body2" color="#64748b">
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {previewEntries.map(renderEntry)}

        {remainingCount > 0 ? (
          <Button
            onClick={() => setFullListOpen(true)}
            variant="text"
            sx={{
              mt: 0.25,
              py: 1.1,
              borderRadius: 2,
              bgcolor: `${color}0d`,
              color,
              fontWeight: 800,
              textTransform: "none",
              border: `1px solid ${color}1f`,
              "&:hover": { bgcolor: `${color}18` },
            }}
          >
            View +{remainingCount} more
          </Button>
        ) : null}
      </Box>

      <Dialog
        open={fullListOpen}
        onClose={() => setFullListOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15,23,42,0.22)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#0f172a", fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
              All {entries.length} ranked items
            </Typography>
          </Box>
          <IconButton
            onClick={() => setFullListOpen(false)}
            aria-label={`Close ${title}`}
            sx={{
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {entries.map(renderEntry)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setFullListOpen(false)}
            variant="contained"
            disableElevation
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AnalyticsSummaryCard = ({ icon, label, value, helper, color }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      border: "1px solid #e2e8f0",
      borderRadius: 2.5,
      bgcolor: "#ffffff",
      boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    }}
  >
    <CardContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: `${color}14`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 800,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "#0f172a",
              fontWeight: 800,
              lineHeight: 1.15,
              mt: 0.25,
            }}
          >
            {value}
          </Typography>
          {helper ? (
            <Typography
              variant="caption"
              sx={{ color: "#94a3b8", fontWeight: 600 }}
            >
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// --- REACT COMPONENT ---
const OesExplorer = () => {
  // Fetch OES Submissions Data containing { exhibitors, oesSubmissions }
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-oes-submissions`
  );

  // Fetch Header/Footer URLs dynamically
  const { data: headerFooterData, isLoading: isHeaderFooterLoading } = useApiEp(
    `/event/visitor-reg-email-header-footer`
  );

  const [filterExhibitor, setFilterExhibitor] = useState("All");
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Map Names & Analytics logic
  const { filteredData, analytics, uniqueExhibitors } = useMemo(() => {
    if (!data || !data.oesSubmissions) {
      return { filteredData: [], analytics: {}, uniqueExhibitors: [] };
    }

    const { oesSubmissions, exhibitors = [] } = data;

    // Map function to convert exhibitor _id into company name.
    // Keep this mapping in one place so the table, filter and analytics all
    // consistently show the exhibitor company name instead of submittedBy id.
    const getExName = (id) => {
      if (!id) return "Unknown Exhibitor";

      const found = exhibitors.find((e) => e.username === String(id));

      return (
        found?.companyName?.value ||
        found?.companyName ||
        String(id) ||
        "Unknown Exhibitor"
      );
    };

    // Pre-process submissions to include the mapped name
    const mappedSubmissions = oesSubmissions.map((sub) => ({
      ...sub,
      exhibitorName: getExName(sub.submittedBy),
    }));

    const exCounts = {};
    const supCounts = {};
    const formCounts = {};
    const exhibitorsSet = new Set();

    mappedSubmissions.forEach((sub) => {
      const exhibitor = sub.exhibitorName;
      const supplier = sub.selectedSupplier?.supplierName || "No Supplier";
      const formName = sub.name || sub.formName || "Unnamed Form";

      exhibitorsSet.add(exhibitor);

      exCounts[exhibitor] = (exCounts[exhibitor] || 0) + 1;
      supCounts[supplier] = (supCounts[supplier] || 0) + 1;
      formCounts[formName] = (formCounts[formName] || 0) + 1;
    });

    const filtered =
      filterExhibitor === "All"
        ? mappedSubmissions
        : mappedSubmissions.filter(
            (sub) => sub.exhibitorName === filterExhibitor
          );

    return {
      filteredData: filtered,
      uniqueExhibitors: Array.from(exhibitorsSet).sort(),
      analytics: {
        total: mappedSubmissions.length,
        formCounts,
        exhibitorCounts: exCounts,
        supplierCounts: supCounts,
        totalForms: Object.keys(formCounts).length,
        totalExhibitors: Object.keys(exCounts).length,
        totalSuppliers: Object.keys(supCounts).filter(
          (name) => name !== "No Supplier"
        ).length,
      },
    };
  }, [data, filterExhibitor]);

  const handleOpenEmailDialog = (submission) => {
    setSelectedSubmission(submission);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedSubmission(null);
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          borderRadius: 3,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a8a 58%, #2563eb 100%)",
          boxShadow: "0 18px 45px rgba(30, 64, 175, 0.18)",
        }}
      >
        <Box
          sx={{
            "& button": {
              color: "#ffffff",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
            },
          }}
        >
          <BackBtn />
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, fontSize: { xs: "1.55rem", sm: "2rem" } }}
          >
            OES Submissions Explorer
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.75, color: "rgba(255,255,255,0.75)" }}
          >
            Review submission performance, then inspect every submitted form.
          </Typography>
        </Box>
      </Box>

      {/* Analytics are the default, primary view. */}
      <Box
        component="section"
        aria-labelledby="submission-analytics-title"
        sx={{ mb: 4 }}
      >
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: "#eff6ff",
                color: "#2563eb",
                display: "grid",
                placeItems: "center",
              }}
            >
              <AssessmentIcon />
            </Box>
            <Box>
              <Typography
                id="submission-analytics-title"
                variant="h6"
                sx={{ color: "#0f172a", fontWeight: 800, lineHeight: 1.2 }}
              >
                Submission Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.35 }}>
                A complete overview across forms, exhibitors and suppliers.
              </Typography>
            </Box>
          </Box>
          <Chip
            label="Live overview"
            size="small"
            sx={{
              display: { xs: "none", sm: "flex" },
              bgcolor: "#ecfdf5",
              color: "#047857",
              fontWeight: 700,
              border: "1px solid #a7f3d0",
            }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsSummaryCard
              icon={<SummarizeOutlinedIcon />}
              label="Total submissions"
              value={analytics.total || 0}
              helper="All OES submissions"
              color="#2563eb"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsSummaryCard
              icon={<DescriptionOutlinedIcon />}
              label="Forms submitted"
              value={analytics.totalForms || 0}
              helper="Forms with a response"
              color="#7c3aed"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsSummaryCard
              icon={<GroupsOutlinedIcon />}
              label="Exhibitors"
              value={analytics.totalExhibitors || 0}
              helper="Unique exhibitors"
              color="#0891b2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsSummaryCard
              icon={<StorefrontOutlinedIcon />}
              label="Suppliers"
              value={analytics.totalSuppliers || 0}
              helper="Unique suppliers selected"
              color="#059669"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          {[
            {
              title: "Submissions by Form",
              subtitle: "Responses received for each form",
              count: analytics.totalForms || 0,
              counts: analytics.formCounts,
              color: "#7c3aed",
              tint: "#f5f3ff",
              emptyText: "No form submissions found",
              icon: <DescriptionOutlinedIcon fontSize="small" />,
            },
            {
              title: "By Exhibitor",
              subtitle: "Companies ranked by response volume",
              count: analytics.totalExhibitors || 0,
              counts: analytics.exhibitorCounts,
              color: "#0891b2",
              tint: "#ecfeff",
              emptyText: "No exhibitor submissions found",
              icon: <GroupsOutlinedIcon fontSize="small" />,
            },
            {
              title: "Supplier Selection",
              subtitle: "Suppliers ranked by selections",
              count: analytics.totalSuppliers || 0,
              counts: analytics.supplierCounts,
              color: "#059669",
              tint: "#ecfdf5",
              emptyText: "No supplier selections found",
              icon: <StorefrontOutlinedIcon fontSize="small" />,
            },
          ].map((item) => (
            <Grid item xs={12} lg={4} key={item.title}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2.5,
                  overflow: "hidden",
                  boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
                }}
              >
                <Box
                  sx={{
                    px: 2.25,
                    py: 1.75,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.75,
                        bgcolor: item.tint,
                        color: item.color,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#0f172a", fontWeight: 800 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={item.count}
                    size="small"
                    sx={{ bgcolor: item.tint, color: item.color, fontWeight: 800 }}
                  />
                </Box>
                <CardContent sx={{ p: 2.25 }}>
                  <AnalyticsBreakdown
                    title={item.title}
                    counts={item.counts}
                    color={item.color}
                    emptyText={item.emptyText}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* The detailed submission list follows the default analytics view. */}
      <Paper
        component="section"
        aria-labelledby="submission-list-title"
        elevation={0}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(15,23,42,0.06)",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                id="submission-list-title"
                variant="h6"
                sx={{ color: "#0f172a", fontWeight: 800 }}
              >
                Submission List
              </Typography>
              <Chip
                label={filteredData.length}
                size="small"
                sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 800 }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.35 }}>
              Filter submissions by exhibitor and open the full email preview.
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 260 } }}>
            <InputLabel>Exhibitor</InputLabel>
            <Select
              value={filterExhibitor}
              onChange={(event) => setFilterExhibitor(event.target.value)}
              label="Exhibitor"
              sx={{ borderRadius: 2, bgcolor: "#ffffff" }}
            >
              <MenuItem value="All">All exhibitors</MenuItem>
              {uniqueExhibitors.map((exhibitor) => (
                <MenuItem key={exhibitor} value={exhibitor}>
                  {exhibitor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer sx={{ maxHeight: 620 }}>
          <MuiTable stickyHeader sx={{ minWidth: 880 }}>
            <MuiTableHead>
              <MuiTableRow>
                {[
                  "Form Name",
                  "Company (Exhibitor)",
                  "Selected Supplier",
                  "Submitted On",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      bgcolor: "#f8fafc",
                      color: "#475569",
                      fontSize: "0.76rem",
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "#f8fafc",
                    color: "#475569",
                    fontSize: "0.76rem",
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Action
                </TableCell>
              </MuiTableRow>
            </MuiTableHead>
            <MuiTableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <MuiTableRow
                    key={row._id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ color: "#0f172a", fontWeight: 700 }}>
                      {row.name || row.formName || "Unnamed Form"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.exhibitorName}
                        size="small"
                        sx={{
                          maxWidth: 230,
                          bgcolor: "#ecfeff",
                          color: "#0e7490",
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#334155" }}>
                      {row.selectedSupplier?.supplierName || "Not selected"}
                    </TableCell>
                    <TableCell sx={{ color: "#64748b", whiteSpace: "nowrap" }}>
                      {row.submittedOn
                        ? new Date(row.submittedOn).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                            timeZone: "Asia/Kolkata",
                          })
                        : "Not available"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={() => handleOpenEmailDialog(row)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        View submission
                      </Button>
                    </TableCell>
                  </MuiTableRow>
                ))
              ) : (
                <MuiTableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <DescriptionOutlinedIcon
                      sx={{ fontSize: 42, color: "#cbd5e1", mb: 1 }}
                    />
                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                      No submissions found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8", mt: 0.5 }}>
                      Try selecting a different exhibitor.
                    </Typography>
                  </TableCell>
                </MuiTableRow>
              )}
            </MuiTableBody>
          </MuiTable>
        </TableContainer>
      </Paper>

      {/* Email View Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15,23,42,0.22)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            color: "#0f172a",
            fontWeight: 800,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Submission Preview
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", mt: 0.25, wordBreak: "break-word" }}
            >
              {selectedSubmission?.name || selectedSubmission?.formName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseEmailDialog}
            aria-label="Close submission preview"
            sx={{
              flexShrink: 0,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f8fafc", p: { xs: 1.5, sm: 3 } }}>
          {isHeaderFooterLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={4}
            >
              <CircularProgress />
            </Box>
          ) : selectedSubmission ? (
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
              dangerouslySetInnerHTML={{
                __html: generateEmailPreview(
                  selectedSubmission,
                  headerFooterData?.headerUrl,
                  headerFooterData?.footerUrl
                ),
              }}
            />
          ) : (
            <MuPb />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={handleCloseEmailDialog}
            color="primary"
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OesExplorer;
